import { UserState } from './dashboardTypes';

const STORAGE_KEY = 'rr_dev_state_override';

export type StateOverride = UserState | 'auto';

/**
 * Sets the dev state override in sessionStorage
 * @param state - The state to override to, or 'auto' to use computed state
 */
export function setStateOverride(state: StateOverride): void {
  if (state === 'auto') {
    sessionStorage.removeItem(STORAGE_KEY);
  } else {
    sessionStorage.setItem(STORAGE_KEY, state);
  }
}

/**
 * Gets the current state override from sessionStorage
 * @returns The overridden state or 'auto' if none set
 */
export function getStateOverride(): StateOverride {
  if (typeof window === 'undefined') return 'auto';

  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (!stored) return 'auto';

  const validStates: StateOverride[] = ['auto', 'new_user', 'empty_state', 'growth_mode', 'established_user'];
  return validStates.includes(stored as StateOverride) ? (stored as StateOverride) : 'auto';
}

/**
 * Clears the state override
 */
export function clearStateOverride(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Applies state override if set and in dev environment
 * @param computedState - The state computed from metrics
 * @param isProduction - Whether running in production
 * @returns The final state to use
 */
export function applyStateOverride(
  computedState: UserState,
  isProduction: boolean
): UserState {
  // Never apply override in production
  if (isProduction) {
    return computedState;
  }

  const override = getStateOverride();
  return override === 'auto' ? computedState : override;
}

/**
 * Check if an override is currently active
 * @returns true if override is set to something other than 'auto'
 */
export function isOverrideActive(): boolean {
  if (typeof window === 'undefined') return false;
  return getStateOverride() !== 'auto';
}
